import { Box, Card, Grid } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import get from 'lodash/get'
import { cloneElement, useState } from 'react'
import {
  ArrayField,
  Datagrid,
  DateField,
  Identifier,
  Labeled,
  NumberField,
  ReferenceField,
  ResourceComponentPropsWithId,
  Tab,
  TabbedShowLayout,
  TextField,
  UrlField,
  usePermissions,
  useShowController,
} from 'react-admin'
import { InlineBlock } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import {
  isProductionEnv,
  LOG_ROCKET_APP_ID_ANDROID,
  LOG_ROCKET_APP_ID_CONSOLE,
  LOG_ROCKET_APP_ID_IOS,
  LOG_ROCKET_APP_ID_WEB,
} from 'shared/config/config'
import { dividerGray, lightGray1 } from 'shared/theme/colors'

import { ELDConnectionErrors, ELDHOSClocks } from 'components/Common/Compliance'
import { RiskyDriverLabel } from 'components/Common/RiskyDriver'
import { clearUserShippingAddress, updateUserShippingAddress } from '../../api/core'
import { FSM_TRANSITION_RECORD_TYPE, RoleNames } from '../../helpers/constants'
import { ROOT } from '../../helpers/documents'
import { getTeamAndProductName } from '../../helpers/utils'
import { ProductSubscription } from '../../models/subscription'
import { User } from '../../models/user'
import { hasPermission, Permissions } from '../../Permissions'
import Aside from '../Aside/aside'
import { BaseActionDetailButton } from '../Common/ActionButtons'
import { AddressEditDialog } from '../Common/AddressForm'
import { CustomReferenceField } from '../Common/CustomReferenceFields'
import { EarlyLifecycleDriverLabel } from '../Common/EarlyLifecycleDriver'
import { Iframe } from '../Common/IFrame'
import { CopyToClipboardButton } from '../CopyToClipboardButton/CopyToClipboardButton'
import DocumentList from '../Documents/DocumentList'
import {
  CustomBooleanField,
  CustomDateField,
  FullNameField,
  JobsReferenceManyList,
  LabeledTypographyField,
} from '../Fields'
import { FSMButtonToolbar } from '../FSM/Buttons/FSMTransitionButton'
import { TeamEntitlements } from '../Teams/TeamEntitlements'
import { CtOnCtProfilePage } from './UserCtonCtProfile'
import { UserFeatureFlags } from './UserFeatureFlags'
import { UserHomeFeed } from './UserHomeFeed'
import { UserReset } from './UserReset'
import UserSSN from './UserSSN'
import { UserZendeskTickets } from './UserZendeskTickets'

const UserStatusToolbar = (props: ResourceComponentPropsWithId & { record?: User }) => {
  const { record, permissions } = props

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <FSMButtonToolbar
        record={record}
        recordType={FSM_TRANSITION_RECORD_TYPE.driver}
        classes={null}
        isHidden={
          !record ||
          !hasPermission(permissions, Permissions.ChangeCustomUser) ||
          !(record.is_team_driver === true) ||
          !record.driver
        }
      />
      <UserReset record={record} permissions={permissions}>
        {(handleReset) => (
          <>
            <Button
              variant="contained"
              color="primary"
              style={{ height: '36px', marginTop: '16px', marginLeft: '8px' }}
              onClick={handleReset}
            >
              Reset User
            </Button>
          </>
        )}
      </UserReset>
    </div>
  )
}

const CustomUserShow = (
  props: ResourceComponentPropsWithId & { record?: User; children: React.ReactElement }
) => {
  const { basePath, loaded, record, resource, version } = useShowController<User>(props)

  if (!record) {
    return null
  }

  const roleLabels = [
    record.is_staff && 'Staff',
    record?.roles?.includes(RoleNames.admin) && 'Admin',
    record?.roles?.includes(RoleNames.driver) && 'Driver',
    record?.roles?.includes(RoleNames.owner) && 'Business Owner',
  ]
  const userRole = roleLabels.filter(Boolean).join(' • ') || ''

  return loaded ? (
    <>
      <Box mt={4} />
      <Grid container direction="column" spacing={3} alignItems="baseline" justifyContent="flex-start">
        <ELDConnectionErrors record={record} />
        <Grid item container direction="row" justifyContent="space-between" spacing={2}>
          <Grid item container xs={7} direction="row" justifyContent="flex-start" spacing={2}>
            <Grid item container direction="column" xs>
              <Text variant="header2">
                <FlexBox alignItems="center" gap="20px" mt={-1}>
                  {record.first_name} {record.last_name}
                </FlexBox>
              </Text>
              <Grid>
                <CustomReferenceField
                  record={record}
                  source="team"
                  baseUrl="/teams"
                  representationFunction={getTeamAndProductName}
                />
              </Grid>
              {userRole && (
                <Text variant="uppercaseLabel2" mt={1}>
                  {userRole}
                </Text>
              )}
              <Box mt={2} />
              {record.team && (
                <>
                  <TeamEntitlements entitlements={record.team.entitlements} team={record.team} />
                  <Box mt={2} />
                </>
              )}
            </Grid>
            <Grid
              item
              container
              direction="column"
              xs
              wrap="nowrap"
              style={{ borderLeft: `1px solid ${dividerGray}`, paddingLeft: `32px` }}
            >
              <Grid container direction="column" style={{ marginBottom: '20px' }}>
                <Grid container direction="row" style={{ flexWrap: 'nowrap' }}>
                  <Text variant="paragraph1">{record.email}</Text>
                  <CopyToClipboardButton text={record.email} />
                </Grid>
                <Text variant="paragraph1" mt={0.5}>
                  {record.mobile}
                </Text>
              </Grid>
              {record?.driver?.assigned_truck && (
                <Grid
                  container
                  direction="column"
                  xs={3}
                  justifyContent="flex-start"
                  style={{ marginBottom: '20px' }}
                >
                  <Text variant="paragraph2" fontColor={lightGray1} mt={0.5} style={{ lineHeight: '0.5' }}>
                    Default Truck
                  </Text>
                  <Text variant="paragraph1">{record?.driver?.assigned_truck.vin_number}</Text>
                </Grid>
              )}

              {record.driver && record.driver.is_early_cycle_driver && (
                <Grid container>
                  <EarlyLifecycleDriverLabel />
                </Grid>
              )}
              {record.driver && record.driver.is_risky && (
                <Grid container>
                  <RiskyDriverLabel />
                </Grid>
              )}
            </Grid>
            <Grid
              container
              item
              direction="column"
              justifyContent="flex-start"
              xs
              style={{ borderLeft: `1px solid ${dividerGray}`, paddingLeft: `32px` }}
            >
              <ELDHOSClocks record={record} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Card style={{ marginTop: '20px' }}>
            {cloneElement(props.children, {
              basePath,
              record,
              resource,
              version,
            })}
          </Card>
        </Grid>

        <Grid item>
          <UserStatusToolbar record={record} {...props} />
          <Aside record={record} resource="users" />
        </Grid>
      </Grid>
    </>
  ) : null
}

const ShippingAddressButtonSection = ({
  record,
  permissions,
}: ResourceComponentPropsWithId & { record?: User }) => {
  if (!record || !permissions) {
    return null
  }
  const canEditUser = hasPermission(permissions, Permissions.ChangeCustomUser)
  let addressExists = get(record, 'shipping_address')

  return (
    canEditUser && (
      <div style={{ display: 'flex' }}>
        <AddressEditDialog
          isBilling={false}
          submit={updateUserShippingAddress}
          record={record}
          address_name="shipping_address"
        />
        {addressExists && (
          <BaseActionDetailButton
            buttonStyle={{ marginLeft: '20px' }}
            variant="text"
            record={record}
            actionRequest={clearUserShippingAddress}
            actionTitle="Remove Shipping Address"
            actionText="This user's shipping address will be deleted. Are you sure?"
          />
        )}
      </div>
    )
  )
}

const UserShow = (props: ResourceComponentPropsWithId) => {
  const { permissions } = usePermissions()
  const [useNewProfile, setUseNewProfile] = useState(true)

  function renderProfilePageSwitch() {
    return (
      <InlineBlock className="profile-page-switch">
        <FormControlLabel
          control={<Switch checked={useNewProfile} onChange={(e) => setUseNewProfile(e.target.checked)} />}
          label={
            <Text variant="header4" mt={0.5}>
              Show new profile page
            </Text>
          }
        />
      </InlineBlock>
    )
  }

  if (useNewProfile) {
    return (
      <>
        {renderProfilePageSwitch()}
        <CtOnCtProfilePage userId={props.id!} permissions={permissions} />
      </>
    )
  }

  return (
    <>
      {renderProfilePageSwitch()}
      <UserOldProfile {...props} />
    </>
  )
}

const UserOldProfile = (props: ResourceComponentPropsWithId) => {
  const { record, loaded } = useShowController(props)
  const { permissions } = usePermissions()

  const deviceUsageSrc: Identifier | undefined =
    record &&
    record.id &&
    `https://datastudio.google.com/embed/reporting/3791c2f3-a1cf-4f5d-81b8-0ab550ce87ac/page/UqxgC?params=%7B%22df2%22:%22include%25EE%2580%25800%25EE%2580%2580EQ%25EE%2580%2580${record.id}%22%7D`

  const businessIntelligenceWeeklySrc: Identifier | undefined =
    record &&
    record.id &&
    `https://datastudio.google.com/embed/reporting/1842e4d3-2bf9-479d-8859-9f26df88c98a/page/p_nin683mypc?params=%7B%22df135%22:%22include%25EE%2580%25800%25EE%2580%2580EQ%25EE%2580%2580${record.id}%22%7D`

  const logrocketWebSessionsURI = `https://app.logrocket.com/${LOG_ROCKET_APP_ID_WEB}/sessions?u=${record?.id}`
  const logrocketAndroidSessionsURI = `https://app.logrocket.com/${LOG_ROCKET_APP_ID_ANDROID}/sessions?u=${record?.id}`
  const logrocketIosSessionsURI = `https://app.logrocket.com/${LOG_ROCKET_APP_ID_IOS}/sessions?u=${record?.id}`
  const logrocketConsoleSessionsURI = `https://app.logrocket.com/${LOG_ROCKET_APP_ID_CONSOLE}/sessions?u=${record?.id}`
  const isPremium =
    record && record.team && record.team.product_subscription_name === ProductSubscription.PREMIUM
  const canSeeHomeFeed = hasPermission(permissions || [], Permissions.ViewHomeFeed)

  if (!loaded) return <InlineSpinner />

  return (
    <>
      <CustomUserShow {...props}>
        <TabbedShowLayout>
          <Tab label="User Information">
            <TextField source="default_team_membership.driver_status" label="Driver Status" />
            <TextField source="default_team_membership.driver_status_detail" label="Driver Status Detail" />
            <TextField source="default_team_membership.driver_status_reason" label="Driver Status Notes" />
            <FlexBox alignItems="flex-end" gap="20px">
              <LabeledTypographyField label="Id" variant="body2" value={props.id!} />
              <CopyToClipboardButton text={props.id} styles="margin-bottom: 7px;" />
            </FlexBox>
            {isProductionEnv() && (
              <Labeled label="Logrocket sessions for the user">
                <>
                  <FlexBox alignItems="flex-end" gap="20px">
                    <ExternalLink href={logrocketWebSessionsURI}>Logrocket sessions (web)</ExternalLink>
                  </FlexBox>
                  <FlexBox alignItems="flex-end" gap="20px">
                    <ExternalLink href={logrocketAndroidSessionsURI}>
                      Logrocket sessions (android)
                    </ExternalLink>
                  </FlexBox>
                  <FlexBox alignItems="flex-end" gap="20px">
                    <ExternalLink href={logrocketIosSessionsURI}>Logrocket sessions (ios)</ExternalLink>
                  </FlexBox>
                  <FlexBox alignItems="flex-end" gap="20px">
                    <ExternalLink href={logrocketConsoleSessionsURI}>
                      Logrocket sessions (console)
                    </ExternalLink>
                  </FlexBox>
                </>
              </Labeled>
            )}
            <Labeled label="Team (Product)">
              <CustomReferenceField
                record={record}
                source="team"
                baseUrl="/teams"
                representationFunction={getTeamAndProductName}
              />
            </Labeled>
            <Labeled label="Date joined">
              <CustomDateField source="date_joined" />
            </Labeled>
            <Labeled label="Date first completed job pick up">
              <CustomDateField source="driver.date_first_completed_job_pick_up" />
            </Labeled>
            <Labeled label="Date deactivated">
              <CustomDateField source="driver.date_deactivated" />
            </Labeled>
            <Labeled label="Date suspended">
              <CustomDateField source="driver.date_suspended" />
            </Labeled>
            <Labeled label="Date unsuspended">
              <CustomDateField source="driver.date_unsuspended" />
            </Labeled>
            <CustomBooleanField source="is_active" />
            <DateField
              showTime={true}
              source="driver.date_terms_accepted"
              options={{ timeZoneName: 'short' }}
            />
            <TextField source="driver.checkr_candidate_id" />
            <UrlField source="driver.checkr_candidate_profile" />
            {record?.driver?.ssn_encrypted && <UserSSN permissions={permissions} />}
            <Labeled label="Date of birth">
              <CustomDateField source="driver.dob" />
            </Labeled>
            {isPremium && <TextField source="driver.years_dry_van" label="Dry van years of experience" />}
            {isPremium && <TextField source="driver.years_flatbed" label="Flatbed years of experience" />}
            {isPremium && <TextField source="driver.years_reefer" label="Reefer years of experience" />}
            {isPremium && (
              <CustomBooleanField
                source="driver.had_at_fault_incident"
                label="Had one or more at-fault preventable accidents in the past 5 years?"
              />
            )}
            {isPremium && (
              <CustomBooleanField
                source="driver.had_major_violation"
                label="Had any major violations or minor violations in the past 5 years?"
              />
            )}
            {isPremium && (
              <CustomBooleanField
                source="driver.had_privilege_denied"
                label="Had been denied a license, permit, or the privilege to operate a motor vehicle?"
              />
            )}
            {isPremium && (
              <CustomBooleanField
                source="driver.had_privilege_suspended"
                label="Had a license, a permit or any privilege suspended or revoked?"
              />
            )}
            <UserFeatureFlags user={record as any} />
            <Iframe source={deviceUsageSrc} width="1200" height="300" title="Device Usage" />
          </Tab>
          <Tab label="Documents" path="documents">
            <DocumentList
              record={record}
              folderName={ROOT}
              permissions={permissions}
              multipleAttachments={false}
            />
          </Tab>
          <Tab label="Shipping Address">
            <TextField source="shipping_address.line1" label="Line 1" />
            <TextField source="shipping_address.line2" label="Line 2" />
            <TextField source="shipping_address.city" label="City" />
            <TextField source="shipping_address.state" label="State" />
            <TextField source="shipping_address.zip" label="Zip" />
            <ShippingAddressButtonSection permissions={permissions} />
          </Tab>
          <Tab label="Jobs">
            <JobsReferenceManyList target="assigned_to_id" showUser={false} is_premium={isPremium} />
          </Tab>
          <Tab label="Driver Preferences">
            <TextField source="driver.driver_preferences.terminal_city" />
            <TextField source="driver.driver_preferences.terminal_state" />
            <TextField source="driver.driver_preferences.terminal_zip" />
            <NumberField source="driver.driver_preferences.terminal_lat" />
            <NumberField source="driver.driver_preferences.terminal_long" />
            <TextField source="driver.driver_preferences.terminal_timezone" />
            <TextField source="driver.driver_preferences.tractor_type" />
            <TextField source="driver.driver_preferences.equipment" />
            <TextField source="driver.driver_preferences.truck_length_ft" />
            <TextField source="driver.driver_preferences.truck_weight_lb" />
            <TextField source="driver.driver_preferences.miles_per_hour" />
          </Tab>
          <Tab label="Referrals">
            <TextField source="referral_code.human_id" label="Referral Code" />
            <ArrayField source="referrals">
              <Datagrid>
                <ReferenceField source="referree.id" reference="users" label="User" link="show">
                  <FullNameField />
                </ReferenceField>
                <TextField source="status" label="Referral Status" />
              </Datagrid>
            </ArrayField>
          </Tab>
          <Tab label="Zendesk tickets">
            <UserZendeskTickets user={record} />
          </Tab>
          {isPremium && canSeeHomeFeed && (
            <Tab label="Home Feed">
              <UserHomeFeed user={record} />
            </Tab>
          )}
          <Tab label="Business Intelligence">
            <Iframe source={businessIntelligenceWeeklySrc} width="1000" height="800" />
          </Tab>
        </TabbedShowLayout>
      </CustomUserShow>
    </>
  )
}

// TODO: Remove default export
// eslint-disable-next-line import/no-default-export
export default UserShow
